import type { 
  ItineraryItem, 
  ItineraryDay, 
  Location, 
  Itinerary,
  SchedulingPreferences,
  ItineraryValidation 
} from '../types/itinerary';

// Distance calculation using Haversine formula
function calculateDistance(from: Location, to: Location): number {
  const R = 6371; // Earth's radius in km
  const dLat = toRad(to.lat - from.lat);
  const dLon = toRad(to.lng - from.lng);
  const a = 
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(from.lat)) * Math.cos(toRad(to.lat)) * 
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

function toRad(deg: number): number {
  return deg * (Math.PI / 180);
}

// Estimate travel time between two locations (in minutes)
export function calculateTravelTime(from: Location, to: Location, mode: 'walking' | 'driving' = 'driving'): number {
  const distance = calculateDistance(from, to);
  const speedKmH = mode === 'walking' ? 5 : 40; // Walking 5km/h, driving 40km/h
  const timeHours = distance / speedKmH;
  return Math.ceil(timeHours * 60);
}

// Get travel time in ISO duration format (PT2H30M)
export function formatDuration(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `PT${hours}H${mins}M`;
}

// Parse ISO duration to minutes
export function parseDuration(duration: string): number {
  const match = duration.match(/PT(?:(\d+)H)?(?:(\d+)M)?/);
  if (!match) return 0;
  const hours = parseInt(match[1]) || 0;
  const minutes = parseInt(match[2]) || 0;
  return hours * 60 + minutes;
}

// Nearest-neighbor algorithm for route optimization
export function optimizeRoute(locations: Location[]): Location[] {
  if (locations.length <= 1) return locations;
  
  const visited = new Set<Location>();
  const route: Location[] = [];
  let current = locations[0];
  
  while (visited.size < locations.length) {
    route.push(current);
    visited.add(current);
    current = findNearestUnvisited(current, locations, visited);
  }
  
  return route;
}

function findNearestUnvisited(
  current: Location, 
  locations: Location[], 
  visited: Set<Location>
): Location {
  let nearest: Location | null = null;
  let minDistance = Infinity;
  
  for (const location of locations) {
    if (visited.has(location)) continue;
    const distance = calculateDistance(current, location);
    if (distance < minDistance) {
      minDistance = distance;
      nearest = location;
    }
  }
  
  return nearest || locations[0];
}

// Suggest time slots based on opening hours and item duration
export function suggestTimeSlots(
  date: string,
  duration: number,
  preferences: SchedulingPreferences,
  openingHour: number = 9, // 9 AM
  closingHour: number = 18 // 6 PM
): { start: string; end: string } | null {
  const startHour = preferences.startTime || openingHour;
  const endHour = preferences.endTime || closingHour;
  
  const availableMinutes = (endHour - startHour) * 60;
  
  if (duration > availableMinutes) {
    return null; // Item doesn't fit in available time
  }
  
  // Suggest a slot in the middle of the day
  const startBuffer = Math.floor((availableMinutes - duration) / 2);
  const startMinute = startHour * 60 + startBuffer;
  
  const startDate = new Date(date);
  startDate.setHours(Math.floor(startMinute / 60), startMinute % 60, 0, 0);
  
  const endDate = new Date(startDate.getTime() + duration * 60 * 1000);
  
  return {
    start: startDate.toISOString(),
    end: endDate.toISOString(),
  };
}

// Auto-schedule an item based on location and existing items
export function autoScheduleItem(
  item: Omit<ItineraryItem, 'id' | 'startTime' | 'endTime'>,
  existingItems: ItineraryItem[],
  preferences: SchedulingPreferences
): ItineraryItem {
  const id = crypto.randomUUID();
  
  // Find the last item in the day (or use hotel location as starting point)
  const sortedItems = [...existingItems].sort(
    (a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime()
  );
  
  let startTime: Date;
  
  if (sortedItems.length === 0) {
    // First item of the day
    startTime = new Date(item.dayNumber === 1 ? item.tripId : item.location.lat); // Placeholder
    startTime.setHours(preferences.startTime, 0, 0, 0);
  } else {
    const lastItem = sortedItems[sortedItems.length - 1];
    const lastEndTime = new Date(lastItem.endTime);
    
    // Add travel time buffer from last item
    const travelTime = calculateTravelTime(lastItem.location, item.location);
    startTime = new Date(lastEndTime.getTime() + (travelTime + preferences.travelTimeBuffer) * 60 * 1000);
  }
  
  const endTime = new Date(startTime.getTime() + item.duration * 60 * 1000);
  
  return {
    ...item,
    id,
    startTime: startTime.toISOString(),
    endTime: endTime.toISOString(),
  };
}

// Generate days for a trip
export function generateTripDays(
  startDate: string,
  endDate: string
): Array<{ dayNumber: number; date: string }> {
  const days: Array<{ dayNumber: number; date: string }> = [];
  const start = new Date(startDate);
  const end = new Date(endDate);
  
  for (let i = 0; i <= Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)); i++) {
    const date = new Date(start);
    date.setDate(date.getDate() + i);
    days.push({
      dayNumber: i + 1,
      date: date.toISOString().split('T')[0],
    });
  }
  
  return days;
}

// Validate an itinerary
export function validateItinerary(
  itinerary: Itinerary,
  preferences?: SchedulingPreferences
): ItineraryValidation {
  const warnings: string[] = [];
  const errors: string[] = [];
  const suggestions: string[] = [];
  let isValid = true;
  
  for (const day of itinerary.days) {
    const sortedItems = [...day.items].sort(
      (a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime()
    );
    
    for (let i = 0; i < sortedItems.length - 1; i++) {
      const current = sortedItems[i];
      const next = sortedItems[i + 1];
      
      // Check for time overlap
      if (new Date(current.endTime) > new Date(next.startTime)) {
        errors.push(`Day ${day.dayNumber}: "${current.title}" overlaps with "${next.title}"`);
        isValid = false;
      }
      
      // Check for insufficient travel time
      const travelTime = calculateTravelTime(current.location, next.location);
      const gapMinutes = (new Date(next.startTime).getTime() - new Date(current.endTime).getTime()) / (1000 * 60);
      
      if (gapMinutes < travelTime) {
        const bufferNeeded = travelTime - gapMinutes;
        warnings.push(`Day ${day.dayNumber}: Only ${Math.round(gapMinutes)} min between activities, need ~${travelTime} min travel time`);
        
        if (preferences?.travelTimeBuffer && bufferNeeded > preferences.travelTimeBuffer) {
          suggestions.push(`Day ${day.dayNumber}: Add ${Math.round(bufferNeeded - preferences.travelTimeBuffer)} min buffer between "${current.title}" and "${next.title}"`);
        }
      }
    }
    
    // Check for too many activities
    if (preferences && day.items.length > preferences.maxActivitiesPerDay) {
      warnings.push(`Day ${day.dayNumber}: ${day.items.length} activities (max recommended: ${preferences.maxActivitiesPerDay})`);
    }
  }
  
  // Check budget
  if (itinerary.totalCost > 10000) { // Arbitrary threshold for warning
    warnings.push(`Total cost ${itinerary.totalCost} seems high - consider reviewing`);
  }
  
  return {
    isValid,
    warnings,
    errors,
    suggestions,
  };
}

// Calculate total cost for a day
export function calculateDayTotal(day: ItineraryDay): number {
  return day.items.reduce((sum, item) => sum + item.cost, 0);
}

// Calculate total cost for entire itinerary
export function calculateItineraryTotal(itinerary: Itinerary): number {
  return itinerary.days.reduce((sum, day) => sum + calculateDayTotal(day), 0);
}
